import React, { useRef, useState, useEffect } from 'react';
import {
  TComponentConfigType,
  IFilterCommonType,
  TFilterKeyType,
  TDispatchFunc,
} from './types';
import { connect } from 'umi';
import { Button } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import useIntls from '@opm/hooks/useIntls';
import { useStateRef } from '@op/hooks/common';
import useDefaultValue from './hooks/useDefaultValue';
import { createShortIdAndSetUrl, getObjKeysValue, sortConfigByDependencyOrder } from './utils';
import { FilterSessionStorageKey } from './constants/storageKey';
import { useFilterCond } from './hooks/useFilterCond';
import { useUpdateEffect } from 'ahooks';
import classnames from 'classnames';
import HistorySelect from './widgets/HistorySelect';
import { cloneDeep } from 'lodash-es';
import {
  setFilterPointLinkId,
  clearFilterBuriedPoint,
  addSubmitBuriedPoint,
  addHistoryBuriedPoint,
} from './utils/point';
import './index.scss';

type TBackValueProps = {
  valueKeys: string[] | string | { [key: string]: string };
};

function commonBackValue(filterValue: any, props: TBackValueProps) {
  const { valueKeys } = props;
  if (typeof valueKeys === 'string') {
    return { [valueKeys]: filterValue[valueKeys] };
  }

  if (Array.isArray(valueKeys)) {
    return valueKeys.reduce((pre, cur) => {
      return { ...pre, [cur]: filterValue[cur] };
    }, {});
  }

  return getObjKeysValue(valueKeys, filterValue);
}

function checkFilterHidden(
  key: TFilterKeyType,
  hiddenFilters?: TFilterKeyType[] | ((key: TFilterKeyType) => boolean),
) {
  if (!hiddenFilters) return false;
  return Array.isArray(hiddenFilters)
    ? hiddenFilters.includes(key)
    : hiddenFilters(key);
}

function FilterCommon({
  keys,
  value,
  confirm,
  reset,
  init,
  userDefaultValue,
  finallyDealwithDefaultValue,
  shortid,
  createShortId = true,
  backShortId,
  shortIdExtraData,
  onShortIdLoaded,
  useOriDefaultValue = true,
  saveStorage = true,
  backCondConfig,
  realTimeMode = true,
  filterConfigProps,
  extrRequestKeys = [],
  instanceId,
  hasHistory = false,
  hasSaveBtn = false,
  pagePath,
  loadingHeight = 40,
  hiddenFilters,
  dependencyOrder,
  condGameIds,
}: IFilterCommonType) {
  const { intl } = useIntls();
  const [selectFilter, setSelectFilter] = useState<string>();
  const [saveOpen, setSaveOpen] = useState(false);
  const [confirmBtnDisabled, setConfirmDisabled] = useState(false);
  const { loading: condLoading, renderConfig } = useFilterCond({
    keys,
    backCondConfig,
    extrRequestKeys,
    condGameIds,
    filterConfigProps
  });
  async function handleConfirm(
    defaultData = value,
    shortIdFlag = true,
    storageFlag = true,
  ) {
    // 按 dependencyOrder 排序后的配置
    const sortedConfig = sortConfigByDependencyOrder(renderConfig, dependencyOrder);
    const filterValue = sortedConfig.reduce((pre, item) => {
      const { props, backValue = commonBackValue} = item; 
      const data = backValue(defaultData, props as any);
      return { ...pre, ...data };
    }, {});
    confirm(filterValue);

    // 触发过滤器提交埋点，传递过滤器值
    addSubmitBuriedPoint(filterValue);

    if (shortIdFlag && createShortId) {
      let shortIdData = defaultData;
      if (shortIdExtraData) {
        const extra = shortIdExtraData(defaultData, defaultData);
        if (extra) shortIdData = { ...defaultData, ...extra };
      }
      const shortid = await createShortIdAndSetUrl(shortIdData);
      backShortId?.(shortid);
    }

    if (storageFlag && saveStorage) {
      sessionStorage.setItem(
        `${FilterSessionStorageKey}_${instanceId}`,
        JSON.stringify(defaultData),
      );
    }
  }

  const { loading, defaultValue, originalDefaultValue } = useDefaultValue({
    userDefaultValue,
    keys,
    finallyDealwithDefaultValue,
    shortid,
    renderConfig,
    condLoading,
    instanceId,
    dependencyOrder,
    onShortIdLoaded,
    initStore: async (defaultValue: any) => {
      await init(defaultValue);
      if (!realTimeMode) {
        handleConfirm(defaultValue, false, false);
      }
    },
  });
  const updateLoading = useRef(false);
  function changeUpdateLoading() {
    updateLoading.current = true;
    setTimeout(() => {
      updateLoading.current = false;
    }, 1000);
  }
  const isFirst = useRef(true);
  const confirmTimerRef = useRef<NodeJS.Timeout>();

  // 初始化过滤器埋点链接 ID
  useEffect(() => {
    setFilterPointLinkId();
    return () => {
      clearFilterBuriedPoint();
    };
  }, []);

  useUpdateEffect(() => {
    // 清除之前的定时器
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
    }

    // 如果发现时间错误，就直接返回，不进行handleConfirm
    if (value.timeError) {
      setConfirmDisabled(true);
      return;
    }

    setConfirmDisabled(false);
    // 初始化完成后才进行后续操作，避免value为空
    if (!loading && realTimeMode && (value.UPDATE === void 0 || value.UPDATE)) {
      // 延迟执行，让子组件的 useUpdateEffect 先执行
      // 例如当 source 变化导致 timeRange 无效时，TimeRangeSelect 需要先调整 timeRange
      confirmTimerRef.current = setTimeout(() => {
        handleConfirm(value, !isFirst.current, !isFirst.current);
        isFirst.current = false;
      }, 0);
    }

    if (hasHistory && !updateLoading.current) {
      setSelectFilter(undefined);
    }
  }, [value, loading]);

  return (
    <CardCommon
      className={classnames('dbom-filter-common-wrapper', {
        'dbom-real-time-mode': realTimeMode,
      })}
      loading={loading}
      loadingHeight={loadingHeight}
      contentCard={realTimeMode}
    >
      <div className="dbom-filter-common-box">
        <div className="dbom-filter-common-list">
          {hasHistory && (
            <div key="dbom-history" className="dbom-filter-common-item">
              <HistorySelect
                confirm={(value: any) => {
                  addHistoryBuriedPoint();
                  init({ ...originalDefaultValue.current, ...value });
                  changeUpdateLoading();
                }}
                saveOpen={saveOpen}
                close={() => setSaveOpen(false)}
                renderConfig={renderConfig}
                filterValue={value}
                pagePath={pagePath}
                selectFilter={selectFilter}
                setSelectFilter={setSelectFilter}
              ></HistorySelect>
            </div>
          )}
          {renderConfig.map((item: TComponentConfigType<any>) => {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { Component, props, key } = item; 

            return (
              <div
                key={key}
                className="dbom-filter-common-item"
                style={
                  checkFilterHidden(key, hiddenFilters)
                    ? { display: 'none' }
                    : undefined
                }
              >
                <Component 
                  {...props}
                  pointKey={key}
                  realTimeMode={realTimeMode}
                  instanceId={instanceId}
                ></Component>
              </div>
            );
          })}
        </div>
        {!realTimeMode && (
          <div className="dbom-filter-common-buttons">
            <Button
              type="primary"
              disabled={confirmBtnDisabled}
              onClick={() => void handleConfirm()}
            >
              {intl({ id: 'Confirm' })}
            </Button>
            <Button
              onClick={() => {
                const value = useOriDefaultValue
                  ? originalDefaultValue.current
                  : defaultValue;
                const defaultData = cloneDeep(value);

                reset(defaultData);
              }}
            >
              {intl({ id: 'Reset' })}
            </Button>
            {hasSaveBtn && (
              <Button onClick={() => setSaveOpen(true)}>
                {intl({ id: 'Save Filter Setting' })}
              </Button>
            )}
          </div>
        )}
      </div>
    </CardCommon>
  );
}

export default connect(
  (state: any, ownProps: { instanceId: string }) => {
    const { instanceId } = ownProps;
    const filterState = state.opinionMarketingFilter;
    const instanceValue = filterState[instanceId] || {};
    return {
      value: instanceValue,
    };
  },
  (dispatch: TDispatchFunc, ownProps: { instanceId: string }) => {
    const { instanceId } = ownProps;

    return {
      reset: (payload: any) => {
        dispatch({
          type: 'opinionMarketingFilter/RESET_FILTER',
          payload,
          instanceId,
        });
      },
      init: (payload: any) => {
        dispatch({
          type: 'opinionMarketingFilter/INIT_FILTER',
          payload,
          instanceId,
        });
      },
    };
  },
)(FilterCommon);

export function useFilterCommon({
  keys,
  dependencyOrder,
  instanceId,
  defaultLoading = true,
}: {
  keys: TFilterKeyType[];
  dependencyOrder?: TFilterKeyType[];
  instanceId?: string;
  defaultLoading?: boolean;
}) {
  const [loading, setLoading] = useState(defaultLoading);
  const [filterList, setFilterList] = useState<TFilterKeyType[]>(keys);
  const [filterRef, filter, setFilter] = useStateRef<any | null>(null);
  const currentShortId = useRef('');

  function changeFilterList(filter: TFilterKeyType[]) {
    setFilterList(filter);
  }

  function changeFilter(value: any) {
    setFilter(value);
    setLoading(false);
  }

  function backShortId(shortid: string) {
    currentShortId.current = shortid;
  }

  return {
    changeFilterList,
    keys: filterList,
    setFilter: changeFilter,
    filter,
    filterRef,
    backShortId,
    currentShortId: currentShortId.current,
    instanceId: instanceId || 'default',
    loading,
    dependencyOrder,
  };
}
